import { inject, Injectable } from '@angular/core';
import { BooktechAppService } from './booktech-app.service';
import { CartItem } from '../model/cart-item';

export type CheckoutPaymentMethod = 'INVOICE' | 'PAY_ON_ARRIVAL' | 'PAY_ON_DEPARTURE';
export const NATIVE_CART_FORMAT = 'native-v1';
export type CartClass = 'empty' | 'native' | 'legacy' | 'mixed' | 'foreign';
export function classifyCart(cart: any, cid: string): CartClass {
  const items = cart?.items;
  if (!items || !items.length) return 'empty';
  const natives = items.filter((i: any) => i?.isCi === true).length;
  if (!natives) return 'legacy';
  if (natives < items.length) return 'mixed';
  return cart.cid && cart.cid !== cid ? 'foreign' : 'native';
}
export interface CheckoutCustomer {
  name: string;
  email: string;
  telephone: string;
  country: string;
  address1: string;
  zipCode: string;
  city: string;
  acceptEula: boolean;
}
export interface NativeCheckoutRequest {
  attemptId: string;
  paymentMethod: CheckoutPaymentMethod;
  customer: CheckoutCustomer;
  items: {
    id: string;
    productId: number;
    period: { from: string; to: string };
    guests: { counts: number[]; quantity: number };
    accessories: { id: string; relationId: number; productId: number; quantity: number }[];
  }[];
}
export interface CheckoutData {
  paymentMethods: CheckoutPaymentMethod[];
  countries: { code: string; name: string }[];
  currencyCode: string;
}
export interface CheckoutQuote {
  total: number;
  currencyCode: string;
  lines: { id: string; productId: number; name: string; from: string; to: string; quantity: number; amount: number }[];
}
export interface CheckoutResult {
  bookingId: number;
  uuid: string;
  status: string;
  paymentMethod: CheckoutPaymentMethod;
  total: number;
  paid: number;
  currencyCode: string;
}

@Injectable({ providedIn: 'root' })
export class CheckoutApiService {
  private bas = inject(BooktechAppService);

  async data(cid: string): Promise<CheckoutData> {
    return (await this.call(cid, '', null, 'get')).checkout;
  }

  async validate(cid: string, request: NativeCheckoutRequest): Promise<CheckoutQuote> {
    return (await this.call(cid, '/validate', request)).quote;
  }

  async placeOrder(cid: string, checkout: NativeCheckoutRequest, acceptedTotal: number): Promise<CheckoutResult> {
    return (await this.call(cid, '/place-order', { checkout, acceptedTotal })).order;
  }

  async order(cid: string, uuid: string): Promise<CheckoutResult> {
    return (await this.call(cid, `/order/${encodeURIComponent(uuid)}`, null, 'get')).order;
  }

  buildRequest(items: CartItem[], customer: CheckoutCustomer, paymentMethod: CheckoutPaymentMethod, attemptId: string): NativeCheckoutRequest {
    if (items.length !== 1 || items.some(i => !i.isCi || !i.id || i.groupItems?.length || i.packageItems?.length || i.poolUnit
      || i.inputValues?.length || i.period.startTime || i.period.endTime || !i.period.from || !i.period.to)) {
      throw new Error('unsupported_cart');
    }
    return {
      attemptId, paymentMethod, customer: { ...customer },
      items: items.map(i => ({
        id: i.id!, productId: i.productId,
        period: { from: i.period.from!, to: i.period.to! },
        guests: { counts: [...i.guests.counts], quantity: i.guests.quantity },
        accessories: (i.accessories || []).map(a => {
          if (a.selectedTime || a.inputValues?.length || a.relatedProductId == null) throw new Error('unsupported_accessory');
          return { id: `${i.id}/accessory/${a.id}`, relationId: a.id, productId: a.relatedProductId, quantity: a.quantity };
        })
      }))
    };
  }

  private async call(cid: string, path: string, body: unknown, method = 'post'): Promise<any> {
    const response = await this.bas.ws.bjson('public', `checkout/native${path}`, body, { cid, method });
    if (!response?.success) throw Object.assign(new Error(response?.statusCode || 'invalid_checkout'), {
      rejected: typeof response?.statusCode === 'string' && response.statusCode.length > 0,
      code: response?.statusCode
    });
    return response;
  }
}
